import React, { useState, useEffect, useCallback, useRef } from "react";
import { useGoogleLogin, CodeResponse } from "@react-oauth/google";
import { toast } from "react-toastify";
import  {google_drive}  from "../../../../assets/images/Images";
import {
  GoogleTokenData,
  GoogleDriveConfig,
  GoogleDrivePickerData,
  downloadGoogleDriveFile,
  loadGooglePickerScript,
  GOOGLE_DRIVE_SCOPES,
  exchangeAuthCode,
} from "../../../../services/googleDriveService";

export interface GoogleDriveUploadProps {
  /** Callback when files are selected from Google Drive */
  onFileSelect: (files: File[]) => void;
  /** Callback to close the parent modal/popup */
  onClose: () => void;
  /** Google token data from parent (passed via props, not from a new store) */
  googleTokenData?: GoogleTokenData | null;
  /** Auth token for API calls */
  authToken?: string;
  /** Callback to submit Google token to backend */
  onGoogleTokenSubmit?: (tokenData: { google_token: GoogleTokenData }) => void;
  /** Callback to fetch Google token from backend */
  onGoogleTokenFetch?: () => void;
  /** Callback when file IDs are selected (for embeddings) */
  onFileIdsSelected?: (fileIds: string[]) => void;
  /** Custom text for connect button */
  connectText?: string;
  /** Custom text for select button */
  selectText?: string;
  /** Whether the upload is disabled */
  disabled?: boolean;
}

/**
 * GoogleDriveUpload component for ChatBot
 * Allows users to connect to Google Drive and select files for upload
 */
const GoogleDriveUpload: React.FC<GoogleDriveUploadProps> = ({
  onFileSelect,
  onClose,
  googleTokenData,
  authToken,
  onGoogleTokenSubmit,
  onGoogleTokenFetch,
  onFileIdsSelected,
  connectText = "Connect To Drive",
  selectText = "Select From Drive",
  disabled = false,
}) => {
  // Initialize access token from props OR localStorage (check both sources)
  const getInitialToken = (): string | undefined => {
    // First check props
    if (googleTokenData?.token) {
      return googleTokenData.token;
    }
    // Then check localStorage
    const storedToken = localStorage.getItem("access_token");
    return storedToken || undefined;
  };

  const [accessToken, setAccessToken] = useState<string | undefined>(getInitialToken);
  const [pickerInited, setPickerInited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const scrollListenerRef = useRef<(() => void) | null>(null);

  const attachScrollGuard = (savedScrollY: number) => {
    if (scrollListenerRef.current) {
      window.removeEventListener("scroll", scrollListenerRef.current as EventListener);
    }
    const handler = () => {
      window.scrollTo({ top: savedScrollY, left: 0 });
    };
    scrollListenerRef.current = handler;
    window.addEventListener("scroll", handler);
  };

  const detachScrollGuard = () => {
    if (scrollListenerRef.current) {
      window.removeEventListener("scroll", scrollListenerRef.current as EventListener);
      scrollListenerRef.current = null;
    }
  };

  // Get config from environment variables
  const config: GoogleDriveConfig = {
    clientId: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID || "",
    clientSecret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY || "",
    apiKey: process.env.REACT_APP_GOOGLEDRIVE_API_KEY || "",
    // redirectUri: process.env.REACT_APP_SHARE_URL || window.location.origin,
    redirectUri: process.env.REACT_APP_SHARE_URL?.replace(/\/$/,"") || window.location.origin,
  };

  // Load Google Picker API on mount
  useEffect(() => {
    loadGooglePickerScript()
      .then(() => {
        setPickerInited(true);
      })
      .catch((error) => {
        console.error("Failed to load Google Picker:", error);
      });
  }, []);

  // Update access token when googleTokenData changes
  useEffect(() => {
    if (googleTokenData?.token) {
      setAccessToken(googleTokenData.token);
    }
  }, [googleTokenData]);

  // Fetch Google token on mount if callback provided
  useEffect(() => {
    if (onGoogleTokenFetch) {
      onGoogleTokenFetch();
    }
  }, [onGoogleTokenFetch]);

  /**
   * Get token data from all available sources (props, localStorage)
   */
  const getStoredTokenData = useCallback((): {
    token?: string;
    refreshToken?: string;
    expiry?: string;
  } => {
    // Check props first (from Redux via parent)
    if (googleTokenData?.token) {
      return {
        token: googleTokenData.token,
        refreshToken: googleTokenData.refresh_token,
        expiry: googleTokenData.expiry,
      };
    }

    // Then check localStorage
    const storedToken = localStorage.getItem("access_token");
    const storedRefreshToken = localStorage.getItem("refresh_token");
    const storedExpiry = localStorage.getItem("token_expiry");

    if (storedToken) {
      return {
        token: storedToken,
        refreshToken: storedRefreshToken || undefined,
        expiry: storedExpiry || undefined,
      };
    }

    return {};
  }, [googleTokenData]);

  /**
   * Refresh the access token using refresh_token (doesn't need redirect_uri)
   */
  const refreshAccessToken = async (refreshToken: string): Promise<string | null> => {
    try {
      const response = await fetch("https://oauth2.googleapis.com/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          client_id: config.clientId,
          client_secret: config.clientSecret,
          refresh_token: refreshToken,
          grant_type: "refresh_token",
        }),
      });

      const data = await response.json();

      if (data.error) {
        console.error("Error refreshing token:", data.error);
        return null;
      }

      const newAccessToken = data.access_token;
      const newExpiryTime = Date.now() + data.expires_in * 1000;

      // Store in localStorage
      localStorage.setItem("access_token", newAccessToken);
      localStorage.setItem("token_expiry", String(newExpiryTime));
      if (data.refresh_token) {
        localStorage.setItem("refresh_token", data.refresh_token);
      }

      // Notify parent to update Redux store
      if (onGoogleTokenSubmit) {
        const updatedTokenData: GoogleTokenData = {
          token: newAccessToken,
          refresh_token: data.refresh_token || refreshToken,
          token_uri: "https://oauth2.googleapis.com/token",
          client_id: config.clientId,
          client_secret: config.clientSecret,
          scopes: data.scope ? data.scope.split(" ") : [],
          expiry: new Date(newExpiryTime).toISOString(),
        };
        onGoogleTokenSubmit({ google_token: updatedTokenData });
      }

      return newAccessToken;
    } catch (error) {
      console.error("Error refreshing access token:", error);
      return null;
    }
  };

  /**
   * Handle token expiration - check if token is valid, refresh if needed
   * This mirrors the original implementation logic
   */
  const handleTokenExpiration = async (): Promise<string | null> => {
    const tokenData = getStoredTokenData();

    // No token data at all - need to login
    if (!tokenData.token && !tokenData.refreshToken) {
      return null;
    }

    const currentTime = Date.now();
    let expiryTime = 0;

    // Parse expiry time
    if (tokenData.expiry) {
      // Could be ISO string or timestamp
      const parsed = Number(tokenData.expiry);
      expiryTime = isNaN(parsed)
        ? new Date(tokenData.expiry).getTime()
        : parsed;
    }

    // Check if token is still valid (with 5 minute buffer)
    if (tokenData.token && currentTime < expiryTime - 5 * 60 * 1000) {
      console.log("Token is still valid");
      return tokenData.token;
    }

    // Token expired or about to expire, try to refresh
    if (tokenData.refreshToken) {
      console.log("Token expired, refreshing...");
      const newToken = await refreshAccessToken(tokenData.refreshToken);
      if (newToken) {
        setAccessToken(newToken);
        return newToken;
      }
    }

    // No refresh token or refresh failed - need to login
    return null;
  };

  /**
   * Handle picker callback when files are selected
   */
  const pickerCallback = useCallback(
    async (data: GoogleDrivePickerData, accToken: string) => {
      if (data.action === window.google.picker.Action.PICKED && data.docs) {
        setIsLoading(true);

        try {
          // Extract file IDs for embeddings
          const selectedIds = data.docs.map((doc) => doc.id).filter(Boolean);
          if (selectedIds.length && onFileIdsSelected) {
            onFileIdsSelected(selectedIds);
          }

          // Download files
          const filePromises = data.docs.map((doc) =>
            downloadGoogleDriveFile(doc, accToken)
          );
          const files = await Promise.all(filePromises);

          // Filter out failed downloads
          const successfulFiles = files.filter(
            (file): file is File => file !== null
          );
          const failedCount = files.length - successfulFiles.length;

          if (failedCount > 0) {
            toast.warning(
              `${failedCount} file(s) failed to download. ${successfulFiles.length} file(s) ready.`
            );
          }

          if (successfulFiles.length > 0) {
            onFileSelect(successfulFiles);
          } else if (failedCount > 0) {
            toast.error("Failed to download selected files.");
          }
        } catch (error) {
          console.error("Error processing selected files:", error);
          toast.error("Failed to process selected files.");
        } finally {
          detachScrollGuard();
          setIsLoading(false);
          onClose();
        }
      } else if (data.action === "cancel") {
        console.log("User cancelled file selection");
        detachScrollGuard();
        onClose();
      }
    },
    [onFileSelect, onClose, onFileIdsSelected]
  );

  /**
   * Create and display the Google Picker
   */
  const createPicker = useCallback(
    (accToken: string) => {
      if (!accToken) {
        console.error("No access token found");
        return;
      }

      if (!window.google?.picker) {
        console.error("Google Picker not loaded");
        return;
      }

      const savedScrollY = window.scrollY;
      attachScrollGuard(savedScrollY);

      const picker = new window.google.picker.PickerBuilder()
        .addView(window.google.picker.ViewId.DOCS)
        .setOAuthToken(accToken)
        .setDeveloperKey(config.apiKey)
        .setCallback((data: GoogleDrivePickerData) =>
          pickerCallback(data, accToken)
        )
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .build();

      picker.setVisible(true);
    },
    [config.apiKey, pickerCallback]
  );

  /**
   * Handle successful Google login (auth-code flow)
   */
  const onLoginSuccess = useCallback(
    async (codeResponse: Omit<CodeResponse, "error" | "error_description" | "error_uri">) => {
      if (!codeResponse.code) {
        console.error("No authorization code received");
        return;
      }

      const tokenData = await exchangeAuthCode(codeResponse.code, config);

      if (tokenData) {
        // Store in localStorage
        localStorage.setItem("access_token", tokenData.token);
        localStorage.setItem("refresh_token", tokenData.refresh_token);
        localStorage.setItem(
          "token_expiry",
          String(new Date(tokenData.expiry).getTime())
        );

        // Notify parent to store the token
        if (onGoogleTokenSubmit) {
          onGoogleTokenSubmit({ google_token: tokenData });
        }

        setAccessToken(tokenData.token);
        createPicker(tokenData.token);
      }
    },
    [config, onGoogleTokenSubmit, createPicker]
  );

  /**
   * Google login hook
   */
  const loginWithGoogle = useGoogleLogin({
    onSuccess: onLoginSuccess,
    onError: (error) => {
      console.error("Google login failed:", error);
      toast.error("Failed to connect to Google Drive");
    },
    scope: GOOGLE_DRIVE_SCOPES,
    flow: "auth-code",
  });

  useEffect(() => {
    return () => {
      detachScrollGuard();
    };
  }, []);

  /**
   * Handle opening the picker (main entry point)
   * This mirrors the original implementation:
   * 1. First check for existing valid token (props or localStorage)
   * 2. If token expired, try to refresh using refresh_token
   * 3. Only if no token/refresh fails, initiate OAuth login
   */
  const handleOpenPicker = async () => {
    if (disabled || !pickerInited || isLoading) return;

    setIsLoading(true);
    onClose(); // Close the modal immediately like original implementation

    try {
      // Check for valid token or refresh expired token
      const validToken = await handleTokenExpiration();

      if (validToken) {
        // We have a valid token, open the picker directly
        createPicker(validToken);
      } else {
        // No valid token, initiate OAuth login
        console.log("No valid token found, initiating Google login...");
        loginWithGoogle();
      }
    } catch (error) {
      console.error("Error opening picker:", error);
      toast.error("Failed to open Google Drive");
    } finally {
      setIsLoading(false);
    }
  };

  const isConnected = accessToken !== undefined;
  const buttonText = isConnected ? selectText : connectText;

  return (
    <div
      className={`flex items-center gap-[11px] cursor-pointer hover:opacity-80 transition-opacity ${
        disabled || !pickerInited || isLoading
          ? "opacity-50 cursor-not-allowed"
          : ""
      }`}
      onClick={handleOpenPicker}
    >
      <div className="w-[19px] h-[21px] flex-shrink-0">
        <img
          src={google_drive}
          alt="Google Drive"
          className="w-full h-full object-contain"
        />
      </div>
      <p className="text-black text-[13px] m-0 whitespace-nowrap">
        {isLoading ? "Loading..." : buttonText}
      </p>
    </div>
  );
}

export default GoogleDriveUpload;
